import { useState, useCallback, useRef } from 'react';
import { useSensor, useSensors, MouseSensor, TouchSensor } from '@dnd-kit/core';
import { toast } from 'react-toastify';
import { applyHomeIconMove } from '../../services/homeLauncher/launcherLayoutService';

const MOUSE_DRAG_DISTANCE = 8;
const CLICK_SUPPRESS_MS = 250;

/**
 * Manages all drag-and-drop state and event handlers.
 * Extracted from HomeLauncherPage – collision and position logic is identical.
 *
 * Mouse/trackpad can rearrange when `canDrag` is true (defaults to editMode so
 * Studio still requires edit mode). Touch drag stays edit-mode-only so scrolling
 * and long-press edit are unchanged.
 *
 * @param {object} opts
 * @param {array}    opts.icons
 * @param {Function} opts.setIcons
 * @param {boolean}  opts.editMode
 * @param {boolean}  [opts.canDrag]       – defaults to editMode
 * @param {Function} opts.resetEditTimeout
 * @param {number}   opts.gridMax
 * @param {Function} opts.handleReorder   – persist; may return a rejected promise
 */
export default function useLauncherDnD({
  icons,
  setIcons,
  editMode,
  canDrag,
  resetEditTimeout,
  gridMax,
  handleReorder,
}) {
  const allowDrag = canDrag ?? Boolean(editMode);
  const [dragActiveId, setDragActiveId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  const [suppressClick, setSuppressClick] = useState(false);
  const suppressTimerRef = useRef(null);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: allowDrag ? MOUSE_DRAG_DISTANCE : 99999 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: editMode ? 0 : 10000, tolerance: 6 },
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  const markGestureSuppressesAction = useCallback(() => {
    if (suppressTimerRef.current) {
      clearTimeout(suppressTimerRef.current);
      suppressTimerRef.current = null;
    }
    setSuppressClick(true);
  }, []);

  const releaseGestureAfterClickWindow = useCallback(() => {
    if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
    suppressTimerRef.current = setTimeout(() => {
      setIsDragging(false);
      setSuppressClick(false);
      suppressTimerRef.current = null;
    }, CLICK_SUPPRESS_MS);
  }, []);

  const clearDragVisuals = useCallback(() => {
    setDragActiveId(null);
    setDragOverId(null);
  }, []);

  const onDragStart = useCallback((event) => {
    if (!allowDrag) return;
    markGestureSuppressesAction();
    setIsDragging(true);
    setDragActiveId(event?.active?.id || null);
    resetEditTimeout();
  }, [allowDrag, markGestureSuppressesAction, resetEditTimeout]);

  const onDragMove = useCallback((event) => {
    if (!allowDrag) return;
    setDragOverId(event?.over?.id || null);
    resetEditTimeout();
  }, [allowDrag, resetEditTimeout]);

  const onDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!allowDrag) {
      clearDragVisuals();
      releaseGestureAfterClickWindow();
      return;
    }

    markGestureSuppressesAction();

    if (!over) {
      clearDragVisuals();
      resetEditTimeout();
      releaseGestureAfterClickWindow();
      return;
    }

    const previous = icons;
    const { icons: updated, changed } = applyHomeIconMove(icons, {
      movedId: String(active.id),
      overId: String(over.id),
      gridMax,
    });

    if (!changed) {
      clearDragVisuals();
      resetEditTimeout();
      releaseGestureAfterClickWindow();
      return;
    }

    setIcons(updated);
    try {
      await handleReorder(updated);
    } catch (err) {
      setIcons(previous);
      toast.error('Could not save button layout. Your previous layout was restored.');
      console.error('[HomeLauncher] layout persist failed', err);
    }
    resetEditTimeout();
    clearDragVisuals();
    releaseGestureAfterClickWindow();
  }, [
    allowDrag,
    icons,
    setIcons,
    gridMax,
    handleReorder,
    resetEditTimeout,
    markGestureSuppressesAction,
    releaseGestureAfterClickWindow,
    clearDragVisuals,
  ]);

  const onDragCancel = useCallback(() => {
    markGestureSuppressesAction();
    clearDragVisuals();
    resetEditTimeout();
    releaseGestureAfterClickWindow();
  }, [markGestureSuppressesAction, clearDragVisuals, resetEditTimeout, releaseGestureAfterClickWindow]);

  return {
    dragActiveId,
    isDragging,
    dragOverId,
    suppressClick,
    sensors,
    onDragStart,
    onDragMove,
    onDragEnd,
    onDragCancel,
  };
}
